import { useMemo } from 'react';
import { Typography, Box, Avatar, Chip, Stack, Grid } from '@mui/material';
import {
  WorkOutline as WorkIcon,
  MonetizationOn as MoneyIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { DOLLAR, IND_RUPEE } from 'constant';
import { getInitials, toTitleCase } from 'views/Dashboard/common/utils';

/* ----------------------------- helpers ----------------------------- */

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const formatCurrency = (amount, symbol) => {
  if (!Number.isFinite(amount)) return '-';
  return `${symbol}${amount.toLocaleString()}`;
};

const StatCard = ({ icon: Icon, label, value }) => (
  <Box sx={{ p: 1, border: '1px solid #EEF2F7', borderRadius: 1.5 }}>
    <Stack direction='row' spacing={1} alignItems='center'>
      <Icon sx={{ fontSize: 18, color: '#6B7280' }} />
      <Box>
        <Typography variant='caption' sx={{ color: '#6B7280' }}>
          {label}
        </Typography>
        <Typography variant='body2' sx={{ fontWeight: 600 }}>
          {value}
        </Typography>
      </Box>
    </Stack>
  </Box>
);

/* ----------------------------- component ----------------------------- */

const InterestPreview = ({ selectedInterest, fxRate, markupPercent }) => {
  if (!selectedInterest) {
    return (
      <Box my={4} p={1} border='1px dashed #E5E7EB' borderRadius={2}>
        <Typography variant='body2' color='text.secondary' align='center' py={2}>
          Select a hired interest to see details
        </Typography>
      </Box>
    );
  }

  const { gig, company, freelancer } = selectedInterest;

  const {
    baseCurrencySymbol,
    freelancerRate,
    clientRate,
    freelancerFxRate,
    clientFxRate,
    showFx,
  } = useMemo(() => {
    const isNational = company?.margin_segment === 'NATIONAL';
    const baseCurrencySymbol = isNational ? IND_RUPEE : DOLLAR;

    const fx = toNumber(fxRate);
    const markup = toNumber(markupPercent);
    const freelancerRate = toNumber(freelancer?.freelancer_rate);
    const baseClientRate = toNumber(company?.client_rate);

    const derivedClientRate =
      Number.isFinite(freelancerRate) && Number.isFinite(markup)
        ? Math.ceil(freelancerRate * (1 + markup / 100))
        : baseClientRate;

    const applyFx = !isNational && Number.isFinite(fx) && fx > 0;

    return {
      isNational,
      baseCurrencySymbol,
      freelancerRate,
      clientRate: derivedClientRate,
      freelancerFxRate: applyFx ? freelancerRate / fx : undefined,
      clientFxRate: applyFx ? derivedClientRate / fx : undefined,
      showFx: applyFx,
    };
  }, [company, freelancer, fxRate, markupPercent]);

  /* ----------------------------- render ----------------------------- */

  return (
    <Box my={4} p={1}>
      {/* Gig header */}
      <Stack direction='row' spacing={1.5} mb={2} alignItems='center'>
        <Avatar sx={{ width: 40, height: 40, bgcolor: '#E0F2FE', color: '#0369A1' }}>
          <WorkIcon sx={{ fontSize: 18 }} />
        </Avatar>
        <Box minWidth={0}>
          <Typography variant='subtitle1' fontWeight={600} noWrap>
            {gig?.title || 'N/A'}
          </Typography>
          <Typography variant='caption' color='#677084' noWrap>
            {gig?.job_role || 'N/A'}
          </Typography>
        </Box>
      </Stack>

      {/* Tags */}
      <Stack direction='row' spacing={1} flexWrap='wrap' mb={2}>
        {gig?.status && (
          <Chip size='small' color='success' variant='outlined' label={`Status: ${toTitleCase(gig.status)}`} />
        )}
        {gig?.job_type && <Chip size='small' variant='outlined' label={toTitleCase(gig.job_type)} />}
        {gig?.gig_type && <Chip size='small' variant='outlined' label={toTitleCase(gig.gig_type)} />}
        {gig?.gig_type === 'time' && gig?.budget_model && (
          <Chip size='small' variant='outlined' label={toTitleCase(gig.budget_model)} />
        )}
      </Stack>

      {/* Stats */}
      <Grid container spacing={1.5} mb={2}>
        {(gig?.min_hourly_rate || gig?.max_hourly_rate) && (
          <Grid item xs={12} sm={6}>
            <StatCard
              icon={ScheduleIcon}
              label='Hourly Range'
              value={`${formatCurrency(gig?.min_hourly_rate, baseCurrencySymbol)} – ${formatCurrency(
                gig?.max_hourly_rate,
                baseCurrencySymbol
              )}`}
            />
          </Grid>
        )}

        {(gig?.min_budget || gig?.max_budget) && (
          <Grid item xs={12} sm={6}>
            <StatCard
              icon={MoneyIcon}
              label='Budget Range'
              value={`${formatCurrency(gig?.min_budget, baseCurrencySymbol)} – ${formatCurrency(
                gig?.max_budget,
                baseCurrencySymbol
              )}`}
            />
          </Grid>
        )}

        {Number.isFinite(freelancerRate) && (
          <Grid item xs={12} sm={6}>
            <StatCard
              icon={MoneyIcon}
              label='Freelancer Rate (per hour)'
              value={
                <>
                  {formatCurrency(freelancerRate, IND_RUPEE)}
                  {showFx && ` (${formatCurrency(freelancerFxRate, baseCurrencySymbol)})`}
                </>
              }
            />
          </Grid>
        )}

        <Grid item xs={12} sm={6}>
          <StatCard
            icon={MoneyIcon}
            label='Billable Client Rate (per hour)'
            value={
              <>
                {formatCurrency(clientRate, IND_RUPEE)}
                {showFx && ` (${formatCurrency(clientFxRate, baseCurrencySymbol)})`}
              </>
            }
          />
        </Grid>
      </Grid>

      {/* Freelancer */}
      <Stack direction='row' spacing={1.5} mb={2} alignItems='center'>
        <Avatar src={freelancer?.profile_picture} sx={{ width: 40, height: 40, bgcolor: '#FEF3C7', color: '#D97706' }}>
          {getInitials(freelancer?.name)}
        </Avatar>
        <Box minWidth={0}>
          <Typography variant='subtitle1' fontWeight={600} noWrap>
            {freelancer?.name || 'NA'}
          </Typography>
          <Typography variant='caption' color='#677084' noWrap>
            {freelancer?.email || '—'} • {freelancer?.freelancer_role || '—'}
          </Typography>
        </Box>
      </Stack>

      {/* Company */}
      <Stack direction='row' spacing={1.5} alignItems='center'>
        <Avatar sx={{ width: 40, height: 40, bgcolor: '#E8EDFF', color: '#1D4ED8' }}>
          <BusinessIcon sx={{ fontSize: 18 }} />
        </Avatar>
        <Box minWidth={0}>
          <Typography variant='subtitle1' fontWeight={600} noWrap>
            {company?.company_name || 'N/A'}
          </Typography>
          <Typography variant='caption' color='#677084' noWrap>
            Margin Segment: {company?.margin_segment || 'N/A'}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default InterestPreview;
